import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parse } from "date-fns";
import "../../../../assets/css/Calendar.css"; // Your custom styles

const MonthYearPicker = ({
  name,
  label,
  ariaLabel = "",
  value, // expected: "YYYY-MM"
  handleChange, // function to receive "YYYY-MM"
  errorMessage = {},
  required = false,
  disabled = false,
  minDate, // JS Date object
  maxDate, // JS Date object
  "aria-label": ariaLabelProp,
  "aria-required": ariaRequiredProp,
}) => {
  const isRequired = ariaRequiredProp !== undefined ? ariaRequiredProp : required;
  const ariaLabelText =
    ariaLabelProp ||
    ariaLabel ||
    (typeof label === "string" && label.trim() ? label.trim() : "") ||
    (typeof name === "string" && name.trim() ? name.trim() : "") ||
    "Select month and year";

  const [selectedDate, setSelectedDate] = useState(null);
  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [isYearOpen, setIsYearOpen] = useState(false);
  const inputRef = useRef(null);

  // Parse string value to JS Date when prop changes
  useEffect(() => {
    if (value) {
      const parsed = parse(value, "yyyy-MM", new Date());
      setSelectedDate(parsed);
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  // Handle date selection
  const onDateChange = (date) => {
    setSelectedDate(date);
    if (date) {
      const formatted = format(date, "yyyy-MM");
      handleChange(formatted);
    } else {
      handleChange("");
    }
  };

  return (
    <div className="datepicker-container">
      {label && (
        <label className="col-md-3 w-100 form-label inmate-label">
          {label}
          {required && <span className="text-danger">*</span>}
        </label>
      )}

      <DatePicker
        ref={inputRef}
        selected={selectedDate}
        onChange={onDateChange}
        dateFormat="MM/yyyy"
        showMonthYearPicker
        placeholderText="MM/YYYY"
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        autoComplete="off"
        customInput={
          <input
            className={`form-control fs-5 fw-normal ${
              errorMessage[name] ? "is-invalid" : ""
            }`}
            value={selectedDate ? format(selectedDate, "MM/yyyy") : ""}
            readOnly
            aria-label={ariaLabelText}
            aria-required={isRequired}
            placeholder="Select month/year"
            onContextMenu={(e) => e.preventDefault()}
          />
        }
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
        }) => {
          const months = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ];
          const currentYear = date.getFullYear();
          const currentMonth = date.getMonth();

          const minYear = minDate ? minDate.getFullYear() : currentYear - 100;
          const maxYear = maxDate ? maxDate.getFullYear() : currentYear + 100;

          const minMonth =
            minDate && currentYear === minYear ? minDate.getMonth() : 0;
          const maxMonth =
            maxDate && currentYear === maxYear ? maxDate.getMonth() : 11;

          const allowedMonths = months.filter((_, index) => {
            if (currentYear === minYear && currentYear === maxYear) {
              return index >= minMonth && index <= maxMonth;
            }
            if (currentYear === minYear) return index >= minMonth;
            if (currentYear === maxYear) return index <= maxMonth;
            return true;
          });

          const years = Array.from(
            { length: maxYear - minYear + 1 },
            (_, i) => minYear + i
          );

          return (
            <div className="custom-header-wrapper">
              <button
                className="csc-month-arrow-icon"
                onClick={decreaseMonth}
                disabled={currentYear === minYear && currentMonth === minMonth}
              >
                {"<"}
              </button>

              {/* Month Dropdown */}
              <div className="custom-dropdown month-dropdown">
                <div
                  className="dropdown-toggle"
                  onClick={() => {
                    setIsMonthOpen(!isMonthOpen);
                    setIsYearOpen(false);
                  }}
                >
                  {months[currentMonth]}
                  <span className="arrow-icon-dropdown">
                    {/* {isMonthOpen ? "▲" : "▼"} */}
                  </span>
                </div>
                {isMonthOpen && (
                  <div className="dropdown-menu scrollable">
                    {allowedMonths.map((month, index) => {
                      const isSelected = months.indexOf(month) === currentMonth;
                      return (
                        <div
                          key={index}
                          className={`dropdown-item ${
                            isSelected ? "selected" : ""
                          }`}
                          onClick={() => {
                            changeMonth(months.indexOf(month));
                            setIsMonthOpen(false);
                          }}
                        >
                          {month}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Year Dropdown */}
              <div className="custom-dropdown year-dropdown">
                <div
                  className="dropdown-toggle"
                  onClick={() => {
                    setIsYearOpen(!isYearOpen);
                    setIsMonthOpen(false);
                  }}
                >
                  {currentYear}
                  <span className="arrow-icon-dropdown">
                    {/* {isYearOpen ? "▲" : "▼"} */}
                  </span>
                </div>
                {isYearOpen && (
                  <div className="dropdown-menu scrollable">
                    {years.map((year) => {
                      const isSelected = year === currentYear;
                      return (
                        <div
                          key={year}
                          className={`dropdown-item ${
                            isSelected ? "selected" : ""
                          }`}
                          onClick={() => {
                            changeYear(year);
                            setIsYearOpen(false);
                          }}
                        >
                          {year}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <button
                className="csc-month-arrow-icon"
                onClick={increaseMonth}
                disabled={currentYear === maxYear && currentMonth === maxMonth}
              >
                {">"}
              </button>
            </div>
          );
        }}
        portalId="root"
        popperPlacement="top-start"
      />

      {errorMessage[name] && (
        <div className="text-danger fw-700 fs-6">{errorMessage[name]}</div>
      )}
    </div>
  );
};

export default MonthYearPicker;
